import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  ArrowLeft, Calendar, MapPin, Users, Package, CheckSquare, 
  FolderOpen, Activity, Plus, Loader2, Upload, Trash2, 
  Clock, User, AlertTriangle, CheckCircle2, XCircle, 
  Send, MoreHorizontal, Edit, FileText, Image, Video,
  Folder, ChevronRight, ExternalLink, Bell, Briefcase,
  DollarSign, Receipt, CreditCard
} from 'lucide-react';
import EventVendorsPanel from '../components/EventVendorsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TASK_STATUSES = [
  { value: 'todo', label: 'To Do', color: 'bg-slate-500' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-500' },
  { value: 'review', label: 'Review', color: 'bg-amber-500' },
  { value: 'done', label: 'Done', color: 'bg-green-500' },
  { value: 'blocked', label: 'Blocked', color: 'bg-red-500' }
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'text-muted-foreground' },
  { value: 'medium', label: 'Medium', color: 'text-blue-500' },
  { value: 'high', label: 'High', color: 'text-amber-500' },
  { value: 'urgent', label: 'Urgent', color: 'text-red-500' }
];

export default function WorkspaceDetailPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [workspace, setWorkspace] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [vendorAssignments, setVendorAssignments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [staff, setStaff] = useState([]);
  const [availableVendors, setAvailableVendors] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialogs
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [showStaffDialog, setShowStaffDialog] = useState(false);
  const [showServiceDetailDialog, setShowServiceDetailDialog] = useState(false);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedServiceItem, setSelectedServiceItem] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  
  // Vendor invoices
  const [vendorInvoices, setVendorInvoices] = useState([]);
  
  // Service item notes
  const [serviceItemNotes, setServiceItemNotes] = useState({});
  
  // Purchase form
  const [purchaseForm, setPurchaseForm] = useState({
    amount: '',
    event_time: '',
    what_to_expect: '',
    what_we_provide: '',
    special_requirements: '',
    notes: ''
  });
  
  // Form states
  const [taskForm, setTaskForm] = useState({
    title: '', description: '', status: 'todo', priority: 'medium',
    assignee_id: '', due_date: '', service_item_id: ''
  });
  const [newFolderName, setNewFolderName] = useState('');
  const [uploading, setUploading] = useState(false);

  const fetchWorkspace = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/workspaces/${workspaceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkspace(response.data.workspace);
      setTasks(response.data.tasks || []);
      setFiles(response.data.files || []);
      setFolders(response.data.folders || []);
      setVendorAssignments(response.data.vendor_assignments || []);
      setActivities(response.data.activities || []);
      setStaff(response.data.staff || []);
      
      // Fetch vendor invoices
      try {
        const invoicesRes = await axios.get(`${API}/workspaces/${workspaceId}/vendor-invoices`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setVendorInvoices(invoicesRes.data.invoices || []);
      } catch (err) {
        console.log('No invoices yet');
        setVendorInvoices([]);
      }
    } catch (error) {
      console.error('Failed to fetch workspace:', error);
      if (error.response?.status === 404) {
        navigate('/admin/workspaces');
      }
    } finally {
      setLoading(false);
    }
  }, [workspaceId, token, navigate]);

  useEffect(() => {
    fetchWorkspace();
    fetchAvailableVendors();
  }, [fetchWorkspace]);

  const fetchAvailableVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors/available`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableVendors(response.data.vendors || []);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    }
  };

  // Task functions
  const handleCreateTask = async () => {
    try {
      await axios.post(
        `${API}/workspaces/${workspaceId}/tasks`,
        taskForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowTaskDialog(false);
      setTaskForm({ title: '', description: '', status: 'todo', priority: 'medium', assignee_id: '', due_date: '', service_item_id: '' });
      fetchWorkspace();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create task');
    }
  };

  const handleUpdateTask = async () => {
    try {
      await axios.put(
        `${API}/workspaces/${workspaceId}/tasks/${editingTask.task_id}`,
        taskForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowTaskDialog(false);
      setEditingTask(null);
      fetchWorkspace();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Delete this task?')) return;
    try {
      await axios.delete(`${API}/workspaces/${workspaceId}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchWorkspace();
    } catch (error) {
      alert('Failed to delete task');
    }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      await axios.put(
        `${API}/workspaces/${workspaceId}/tasks/${taskId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchWorkspace();
    } catch (error) {
      alert('Failed to update task');
    }
  };

  // File functions
  const handleFileUpload = async (event) => {
    const uploadedFiles = event.target.files;
    if (!uploadedFiles?.length) return;
    
    setUploading(true);
    try {
      for (const file of uploadedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        if (currentFolderId) formData.append('folder_id', currentFolderId);
        
        await axios.post(
          `${API}/workspaces/${workspaceId}/files`,
          formData,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
        );
      }
      fetchWorkspace();
    } catch (error) {
      alert('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!confirm('Delete this file?')) return;
    try {
      await axios.delete(`${API}/workspaces/${workspaceId}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchWorkspace();
    } catch (error) {
      alert('Failed to delete file');
    }
  };

  // Folder functions
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await axios.post(
        `${API}/workspaces/${workspaceId}/folders`,
        { name: newFolderName, parent_id: currentFolderId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowFolderDialog(false);
      setNewFolderName('');
      fetchWorkspace();
    } catch (error) {
      alert('Failed to create folder');
    }
  };

  // Vendor functions
  const handleAssignVendor = async (vendorId) => {
    if (!selectedServiceItem) return;
    try {
      await axios.post(
        `${API}/workspaces/${workspaceId}/vendors`,
        {
          vendor_id: vendorId,
          service_item_id: selectedServiceItem.service_item_id || selectedServiceItem.item_id,
          service_name: selectedServiceItem.name,
          notify: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowVendorDialog(false);
      setSelectedServiceItem(null);
      fetchWorkspace();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to assign vendor');
    }
  };

  const handleNotifyVendor = async (assignmentId) => {
    try {
      await axios.post(
        `${API}/workspaces/${workspaceId}/vendors/${assignmentId}/notify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Notification sent!');
      fetchWorkspace();
    } catch (error) {
      alert('Failed to send notification');
    }
  };

  const handleUpdateVendorStatus = async (assignmentId, status) => {
    try {
      await axios.put(
        `${API}/workspaces/${workspaceId}/vendors/${assignmentId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchWorkspace();
    } catch (error) {
      alert('Failed to update vendor status');
    }
  };

  // Purchase/Invoice functions
  const handleOpenPurchaseDialog = (assignment, serviceItem) => {
    setSelectedAssignment(assignment);
    setSelectedServiceItem(serviceItem);
    
    // Pre-fill amount from service item if available
    const suggestedAmount = serviceItem?.total_price || serviceItem?.price || '';
    
    setPurchaseForm({
      amount: suggestedAmount ? suggestedAmount.toString() : '',
      event_time: '',
      what_to_expect: serviceItem?.description || '',
      what_we_provide: '',
      special_requirements: '',
      notes: ''
    });
    setShowPurchaseDialog(true);
  };

  const handleCreatePurchase = async () => {
    if (!selectedAssignment) return;
    
    if (!purchaseForm.amount || parseFloat(purchaseForm.amount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      await axios.post(
        `${API}/workspaces/${workspaceId}/vendor-invoices`,
        {
          vendor_assignment_id: selectedAssignment.assignment_id,
          amount: parseFloat(purchaseForm.amount),
          event_time: purchaseForm.event_time,
          what_to_expect: purchaseForm.what_to_expect,
          what_we_provide: purchaseForm.what_we_provide,
          special_requirements: purchaseForm.special_requirements,
          notes: purchaseForm.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowPurchaseDialog(false);
      setSelectedAssignment(null);
      setPurchaseForm({ amount: '', event_time: '', what_to_expect: '', what_we_provide: '', special_requirements: '', notes: '' });
      fetchWorkspace();
      alert('Purchase order created successfully!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create purchase order');
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await axios.post(
        `${API}/vendor-invoices/${invoiceId}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchWorkspace();
      alert('Invoice sent to vendor!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to send invoice');
    }
  };

  const handleMarkInvoicePaid = async (invoiceId) => {
    if (!confirm('Mark this invoice as paid? This will credit the vendor\'s account.')) return;
    try {
      await axios.post(
        `${API}/vendor-invoices/${invoiceId}/mark-paid`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchWorkspace();
      alert('Invoice marked as paid!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to mark invoice paid');
    }
  };

  const handleCancelInvoice = async (invoiceId) => {
    if (!confirm('Cancel this invoice?')) return;
    try {
      await axios.delete(
        `${API}/vendor-invoices/${invoiceId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchWorkspace();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to cancel invoice');
    }
  };

  const getInvoiceForAssignment = (assignmentId) => {
    return vendorInvoices.find(inv => inv.vendor_assignment_id === assignmentId);
  };

  const getInvoiceStatusBadge = (status) => {
    switch (status) {
      case 'paid': return <Badge className="bg-green-500">Paid</Badge>;
      case 'accepted': return <Badge className="bg-blue-500">Accepted</Badge>;
      case 'sent': return <Badge className="bg-amber-500">Sent</Badge>;
      case 'declined': return <Badge variant="destructive">Declined</Badge>;
      case 'cancelled': return <Badge variant="outline" className="text-muted-foreground">Cancelled</Badge>;
      default: return <Badge variant="outline">Draft</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image': return <Image className="h-5 w-5 text-green-500" />;
      case 'video': return <Video className="h-5 w-5 text-purple-500" />;
      case 'document': return <FileText className="h-5 w-5 text-blue-500" />;
      default: return <FileText className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getVendorStatusBadge = (status) => {
    switch (status) {
      case 'accepted': return <Badge className="bg-green-500">Accepted</Badge>;
      case 'declined': return <Badge variant="destructive">Declined</Badge>;
      case 'notified': return <Badge className="bg-amber-500">Notified</Badge>;
      case 'completed': return <Badge className="bg-blue-500">Completed</Badge>;
      default: return <Badge variant="outline">Pending</Badge>;
    }
  };

  // Get files in current folder
  const currentFolderFiles = files.filter(f => f.folder_id === currentFolderId);
  const currentSubfolders = folders.filter(f => f.parent_id === currentFolderId);

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <p>Workspace not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <Button variant="ghost" onClick={() => navigate('/admin/workspaces')} className="w-fit">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Workspaces
            </Button>
            <Button variant="outline" onClick={() => navigate(`/my-events/${workspace.booking_id}/manage`)} data-testid="view-booking-btn" className="w-fit">
              <FileText className="mr-2 h-4 w-4" /> <span className="hidden sm:inline">View </span>Booking/Invoice
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3" data-testid="workspace-title">
                <Briefcase className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-500 flex-shrink-0" />
                <span className="break-words">{workspace.title}</span>
              </h1>
              <p className="text-muted-foreground mt-1 text-sm sm:text-base">
                {workspace.client_name} • <span className="break-all">{workspace.client_email}</span>
              </p>
            </div>
            <Badge className={`${workspace.status === 'active' ? 'bg-green-500' : 'bg-slate-500'} w-fit`}>
              {workspace.status}
            </Badge>
          </div>
        </div>

        {/* Event Info Bar */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-8">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Event Date</p>
                  <p className="font-medium text-sm sm:text-base truncate">{formatDate(workspace.event_date)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Location</p>
                  <p className="font-medium text-sm sm:text-base truncate">{workspace.event_location || 'TBD'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Guests</p>
                  <p className="font-medium text-sm sm:text-base">{workspace.guest_count}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                <div>
                  <p className="text-xs text-muted-foreground">Services</p>
                  <p className="font-medium text-sm sm:text-base">{workspace.service_items?.length || 0}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="overview" data-testid="tab-overview" className="text-xs sm:text-sm">
              <Package className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" /> <span className="hidden sm:inline">Overview</span><span className="sm:hidden">Info</span>
            </TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks" className="text-xs sm:text-sm">
              <CheckSquare className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" /> Tasks ({tasks.length})
            </TabsTrigger>
            <TabsTrigger value="files" data-testid="tab-files" className="text-xs sm:text-sm">
              <FolderOpen className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" /> Files ({files.length})
            </TabsTrigger>
            <TabsTrigger value="vendors" data-testid="tab-vendors" className="text-xs sm:text-sm">
              <Users className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" /> <span className="hidden sm:inline">Vendors</span><span className="sm:hidden">V</span> ({vendorAssignments.length})
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity" className="text-xs sm:text-sm">
              <Activity className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" /> <span className="hidden sm:inline">Activity</span><span className="sm:hidden">Log</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Service Items */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Service Items</CardTitle>
                    <CardDescription>Services included in this booking</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {workspace.service_items?.map((item, idx) => {
                        const itemId = item.service_item_id || item.item_id;
                        const assignment = vendorAssignments.find(a => a.service_item_id === itemId);
                        const staffAssignment = vendorAssignments.find(a => a.service_item_id === itemId && a.is_staff);
                        const itemTasks = tasks.filter(t => t.service_item_id === itemId);
                        const completedTasks = itemTasks.filter(t => t.status === 'done').length;
                        
                        return (
                          <div key={idx} className="p-4 border rounded-lg hover:border-indigo-300 transition-colors" data-testid={`service-item-${idx}`}>
                            {/* Header Row */}
                            <div className="flex items-start justify-between mb-3">
                              <div 
                                className="flex-1 cursor-pointer"
                                onClick={() => {
                                  setSelectedServiceItem(item);
                                  setShowServiceDetailDialog(true);
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold">{item.name}</h4>
                                  <Badge variant="outline">{item.category}</Badge>
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">
                                  Qty: {item.quantity} • ${item.total_price?.toFixed(2) || item.price}
                                </p>
                                {item.description && (
                                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.description}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {assignment ? (
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium">{assignment.vendor_name}</span>
                                    {getVendorStatusBadge(assignment.status)}
                                  </div>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedServiceItem(item);
                                      setShowVendorDialog(true);
                                    }}
                                    data-testid={`assign-vendor-${idx}`}
                                  >
                                    <Users className="mr-1 h-4 w-4" /> Assign Vendor
                                  </Button>
                                )}
                              </div>
                            </div>

                            {/* Action Buttons Row */}
                            <div className="flex items-center gap-2 mb-3">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedServiceItem(item);
                                  setShowStaffDialog(true);
                                }}
                                data-testid={`assign-staff-${idx}`}
                              >
                                <User className="mr-1 h-4 w-4" /> 
                                {staffAssignment ? 'Change Staff' : 'Assign Staff'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setEditingTask(null);
                                  setTaskForm({
                                    title: '', 
                                    description: '', 
                                    status: 'todo', 
                                    priority: 'medium',
                                    assignee_id: '', 
                                    due_date: '', 
                                    service_item_id: itemId
                                  });
                                  setShowTaskDialog(true);
                                }}
                                data-testid={`add-task-${idx}`}
                              >
                                <CheckSquare className="mr-1 h-4 w-4" /> Add Task
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedServiceItem(item);
                                  setShowServiceDetailDialog(true);
                                }}
                              >
                                <ExternalLink className="mr-1 h-4 w-4" /> Details
                              </Button>
                            </div>

                            {/* Progress Row */}
                            <div className="flex items-center justify-between pt-2 border-t text-xs">
                              <div className="flex items-center gap-4">
                                <span className="text-muted-foreground">
                                  Tasks: {completedTasks}/{itemTasks.length}
                                </span>
                                {assignment && (
                                  <span className="text-muted-foreground">
                                    Vendor: {assignment.status}
                                  </span>
                                )}
                              </div>
                              {itemTasks.length > 0 && (
                                <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-green-500" 
                                    style={{ width: `${itemTasks.length ? (completedTasks / itemTasks.length * 100) : 0}%` }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Quick Stats */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Progress</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Tasks</span>
                        <span>{tasks.filter(t => t.status === 'done').length}/{tasks.length}</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-500" 
                          style={{ width: `${tasks.length ? (tasks.filter(t => t.status === 'done').length / tasks.length * 100) : 0}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Vendors Confirmed</span>
                        <span>{vendorAssignments.filter(v => v.status === 'accepted').length}/{vendorAssignments.length}</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-indigo-500" 
                          style={{ width: `${vendorAssignments.length ? (vendorAssignments.filter(v => v.status === 'accepted').length / vendorAssignments.length * 100) : 0}%` }}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Activity */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Recent Activity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {activities.slice(0, 5).map(activity => (
                        <div key={activity.activity_id} className="flex items-start gap-2 text-sm">
                          <div className="w-2 h-2 bg-indigo-500 rounded-full mt-1.5" />
                          <div>
                            <p>{activity.description}</p>
                            <p className="text-xs text-muted-foreground">{formatDateTime(activity.created_at)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Tasks</CardTitle>
                  <CardDescription>Manage tasks for this event</CardDescription>
                </div>
                <Button onClick={() => {
                  setEditingTask(null);
                  setTaskForm({ title: '', description: '', status: 'todo', priority: 'medium', assignee_id: '', due_date: '', service_item_id: '' });
                  setShowTaskDialog(true);
                }} data-testid="add-task-btn">
                  <Plus className="mr-2 h-4 w-4" /> Add Task
                </Button>
              </CardHeader>
              <CardContent>
                {/* Task Board */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {TASK_STATUSES.slice(0, 4).map(statusInfo => {
                    const statusTasks = tasks.filter(t => t.status === statusInfo.value);
                    return (
                      <div key={statusInfo.value} className="space-y-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${statusInfo.color}`} />
                          <span className="font-medium text-sm">{statusInfo.label}</span>
                          <span className="text-xs text-muted-foreground">({statusTasks.length})</span>
                        </div>
                        <div className="space-y-2 min-h-[200px] p-2 bg-muted/30 rounded-lg">
                          {statusTasks.map(task => (
                            <Card key={task.task_id} className="p-3 cursor-pointer hover:shadow-md" data-testid={`task-${task.task_id}`}>
                              <div className="flex items-start justify-between">
                                <h4 className="font-medium text-sm">{task.title}</h4>
                                <Select
                                  value={task.status}
                                  onValueChange={(v) => handleTaskStatusChange(task.task_id, v)}
                                >
                                  <SelectTrigger className="w-8 h-6 p-0 border-0">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {TASK_STATUSES.map(s => (
                                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                                {task.priority && (
                                  <Badge variant="outline" className="text-xs">
                                    {task.priority}
                                  </Badge>
                                )}
                                {task.due_date && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {formatDate(task.due_date)}
                                  </span>
                                )}
                              </div>
                              {task.assignee_name && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  <User className="h-3 w-3 inline mr-1" />
                                  {task.assignee_name}
                                </p>
                              )}
                              <div className="flex gap-1 mt-2">
                                <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => {
                                  setEditingTask(task);
                                  setTaskForm(task);
                                  setShowTaskDialog(true);
                                }}>
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-6 px-2 text-destructive" onClick={() => handleDeleteTask(task.task_id)}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </Card>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Files Tab */}
          <TabsContent value="files">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Files</CardTitle>
                  <CardDescription>
                    {currentFolderId ? (
                      <Button variant="link" className="p-0 h-auto" onClick={() => setCurrentFolderId(null)}>
                        ← Back to root
                      </Button>
                    ) : 'Upload and organize files for this event'}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowFolderDialog(true)}>
                    <Folder className="mr-2 h-4 w-4" /> New Folder
                  </Button>
                  <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-file-btn">
                    {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                    Upload Files
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileUpload}
                    accept="image/*,video/*,.pdf,.doc,.docx"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {/* Folders */}
                {currentSubfolders.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
                    {currentSubfolders.map(folder => (
                      <div
                        key={folder.folder_id}
                        className="p-4 border rounded-lg hover:bg-muted/50 cursor-pointer text-center"
                        onClick={() => setCurrentFolderId(folder.folder_id)}
                      >
                        <Folder className="h-10 w-10 mx-auto text-amber-500 mb-2" />
                        <p className="text-sm font-medium truncate">{folder.name}</p>
                        <p className="text-xs text-muted-foreground">{folder.folder_type}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Files */}
                {currentFolderFiles.length === 0 && currentSubfolders.length === 0 ? (
                  <div className="text-center py-12">
                    <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No files yet. Upload files to get started.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {currentFolderFiles.map(file => (
                      <div key={file.file_id} className="p-4 border rounded-lg group relative">
                        {file.file_type === 'image' && file.url ? (
                          <img src={file.url} alt={file.name} className="w-full h-24 object-cover rounded mb-2" />
                        ) : (
                          <div className="w-full h-24 bg-muted rounded mb-2 flex items-center justify-center">
                            {getFileIcon(file.file_type)}
                          </div>
                        )}
                        <p className="text-sm font-medium truncate">{file.name}</p>
                        <p className="text-xs text-muted-foreground">{(file.size_bytes / 1024).toFixed(1)} KB</p>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => window.open(file.url, '_blank')}>
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive" onClick={() => handleDeleteFile(file.file_id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vendors Tab */}
          <TabsContent value="vendors">
            <div className="space-y-6">
              <EventVendorsPanel workspaceId={workspaceId} token={token} />
            <Card>
              <CardHeader>
                <CardTitle>Vendor Assignments & Invoices</CardTitle>
                <CardDescription>Manage vendors and purchase orders for each service</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {vendorAssignments.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No vendors assigned yet</p>
                    </div>
                  ) : (
                    vendorAssignments.map(assignment => {
                      const invoice = getInvoiceForAssignment(assignment.assignment_id);
                      const serviceItem = workspace?.service_items?.find(
                        item => (item.service_item_id || item.item_id) === assignment.service_item_id
                      );
                      
                      return (
                        <div key={assignment.assignment_id} className="p-4 border rounded-lg" data-testid={`vendor-assignment-${assignment.assignment_id}`}>
                          {/* Header Row */}
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold">{assignment.vendor_name}</h4>
                                {getVendorStatusBadge(assignment.status)}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                Service: {assignment.service_name} • {assignment.vendor_email}
                              </p>
                              {assignment.notified_at && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Notified: {formatDateTime(assignment.notified_at)}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button size="sm" variant="outline" onClick={() => handleNotifyVendor(assignment.assignment_id)}>
                                <Bell className="mr-1 h-4 w-4" /> Notify
                              </Button>
                              <Select
                                value={assignment.status}
                                onValueChange={(v) => handleUpdateVendorStatus(assignment.assignment_id, v)}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="notified">Notified</SelectItem>
                                  <SelectItem value="accepted">Accepted</SelectItem>
                                  <SelectItem value="declined">Declined</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {/* Invoice Section */}
                          <Separator className="my-3" />
                          <div className="bg-muted/30 rounded-lg p-3">
                            {invoice ? (
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Receipt className="h-4 w-4 text-indigo-500" />
                                    <span className="font-medium">Purchase Order</span>
                                    {getInvoiceStatusBadge(invoice.status)}
                                  </div>
                                  <span className="text-lg font-bold text-green-600">${invoice.amount?.toFixed(2)}</span>
                                </div>
                                {invoice.what_to_expect && (
                                  <p className="text-xs text-muted-foreground">
                                    <span className="font-medium">Deliverables:</span> {invoice.what_to_expect.slice(0, 100)}...
                                  </p>
                                )}
                                <div className="flex items-center gap-2 pt-2">
                                  {invoice.status === 'draft' && (
                                    <>
                                      <Button size="sm" onClick={() => handleSendInvoice(invoice.invoice_id)} data-testid={`send-invoice-${invoice.invoice_id}`}>
                                        <Send className="mr-1 h-4 w-4" /> Send to Vendor
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={() => handleCancelInvoice(invoice.invoice_id)}>
                                        <Trash2 className="mr-1 h-4 w-4" /> Cancel
                                      </Button>
                                    </>
                                  )}
                                  {invoice.status === 'sent' && (
                                    <span className="text-xs text-muted-foreground">Awaiting vendor response...</span>
                                  )}
                                  {invoice.status === 'accepted' && (
                                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleMarkInvoicePaid(invoice.invoice_id)} data-testid={`mark-paid-${invoice.invoice_id}`}>
                                      <CreditCard className="mr-1 h-4 w-4" /> Mark as Paid
                                    </Button>
                                  )}
                                  {invoice.status === 'paid' && (
                                    <div className="flex items-center gap-2 text-green-600">
                                      <CheckCircle2 className="h-4 w-4" />
                                      <span className="text-sm font-medium">Payment Complete</span>
                                      <span className="text-xs text-muted-foreground">• {formatDateTime(invoice.paid_at)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <DollarSign className="h-4 w-4" />
                                  <span className="text-sm">No purchase order yet</span>
                                </div>
                                <Button 
                                  size="sm" 
                                  onClick={() => handleOpenPurchaseDialog(assignment, serviceItem)}
                                  data-testid={`purchase-service-${assignment.assignment_id}`}
                                >
                                  <DollarSign className="mr-1 h-4 w-4" /> Purchase Service
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
            </div>
          </TabsContent>
          <TabsContent value="activity">
            <Card>
              <CardHeader>
                <CardTitle>Activity Feed</CardTitle>
                <CardDescription>All actions taken in this workspace</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {activities.map(activity => (
                    <div key={activity.activity_id} className="flex items-start gap-3 p-3 border-l-2 border-indigo-500">
                      <Activity className="h-5 w-5 text-indigo-500 mt-0.5" />
                      <div>
                        <p>{activity.description}</p>
                        <p className="text-sm text-muted-foreground">
                          {activity.user_name} • {formatDateTime(activity.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Edit Task' : 'New Task'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title</label>
              <Input
                value={taskForm.title}
                onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
                placeholder="Task title"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                placeholder="Task description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Status</label>
                <Select value={taskForm.status} onValueChange={(v) => setTaskForm({...taskForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TASK_STATUSES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Priority</label>
                <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({...taskForm, priority: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Assignee</label>
                <Select value={taskForm.assignee_id} onValueChange={(v) => {
                  const assignee = staff.find(s => s.user_id === v);
                  setTaskForm({...taskForm, assignee_id: v, assignee_name: assignee?.name || ''});
                }}>
                  <SelectTrigger><SelectValue placeholder="Select assignee" /></SelectTrigger>
                  <SelectContent>
                    {staff.map(s => (
                      <SelectItem key={s.user_id} value={s.user_id}>{s.name || s.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Due Date</label>
                <Input
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Service Item</label>
              <Select value={taskForm.service_item_id || "none"} onValueChange={(v) => setTaskForm({...taskForm, service_item_id: v === "none" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="Link to service (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {workspace?.service_items?.map(item => (
                    <SelectItem key={item.service_item_id || item.item_id} value={item.service_item_id || item.item_id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
            <Button onClick={editingTask ? handleUpdateTask : handleCreateTask}>
              {editingTask ? 'Update' : 'Create'} Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Assignment Dialog */}
      <Dialog open={showVendorDialog} onOpenChange={setShowVendorDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Vendor</DialogTitle>
            <DialogDescription>
              Select a vendor for: {selectedServiceItem?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {availableVendors.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No vendors available</p>
            ) : (
              availableVendors.map(vendor => (
                <div key={vendor.vendor_id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">{vendor.business_name}</p>
                    <p className="text-sm text-muted-foreground">{vendor.category} • {vendor.email}</p>
                  </div>
                  <Button size="sm" onClick={() => handleAssignVendor(vendor.vendor_id)}>
                    Assign
                  </Button>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVendorDialog(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Folder Dialog */}
      <Dialog open={showFolderDialog} onOpenChange={setShowFolderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Folder</DialogTitle>
          </DialogHeader>
          <Input
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Folder name"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFolderDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateFolder}>Create Folder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff Assignment Dialog */}
      <Dialog open={showStaffDialog} onOpenChange={setShowStaffDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Staff</DialogTitle>
            <DialogDescription>
              Select a staff member for: {selectedServiceItem?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {staff.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No staff available</p>
            ) : (
              staff.map(member => (
                <div key={member.user_id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">{member.name || member.email}</p>
                    <p className="text-sm text-muted-foreground">{member.role}</p>
                  </div>
                  <Button size="sm" onClick={() => {
                    // Assign staff (similar to vendor but marked as staff)
                    setShowStaffDialog(false);
                    alert(`Assigned ${member.name || member.email} to ${selectedServiceItem?.name}`);
                  }}>
                    Assign
                  </Button>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStaffDialog(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service Detail Dialog */}
      <Dialog open={showServiceDetailDialog} onOpenChange={setShowServiceDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-indigo-500" />
              {selectedServiceItem?.name}
            </DialogTitle>
            <DialogDescription>
              Service details and management
            </DialogDescription>
          </DialogHeader>
          
          {selectedServiceItem && (
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground">Category</label>
                  <p className="font-medium">{selectedServiceItem.category}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Quantity</label>
                  <p className="font-medium">{selectedServiceItem.quantity}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Unit Price</label>
                  <p className="font-medium">${selectedServiceItem.price || selectedServiceItem.base_price}</p>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Total</label>
                  <p className="font-medium">${selectedServiceItem.total_price?.toFixed(2)}</p>
                </div>
              </div>

              <Separator />

              {/* Description / What to Expect */}
              <div>
                <label className="text-sm font-medium">Description / What to Expect</label>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedServiceItem.description || selectedServiceItem.short_description || 'No description provided'}
                </p>
              </div>

              {/* Selected Options */}
              {selectedServiceItem.selected_options?.length > 0 && (
                <div>
                  <label className="text-sm font-medium">Selected Options</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedServiceItem.selected_options.map((opt, i) => (
                      <Badge key={i} variant="secondary">
                        {opt.choice_name || opt.name}: {opt.option || opt.value}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <Separator />

              {/* Vendor Assignment Status */}
              <div>
                <label className="text-sm font-medium">Vendor Assignment</label>
                {(() => {
                  const assignment = vendorAssignments.find(
                    a => a.service_item_id === (selectedServiceItem.service_item_id || selectedServiceItem.item_id)
                  );
                  return assignment ? (
                    <div className="flex items-center gap-2 mt-1">
                      <span>{assignment.vendor_name}</span>
                      {getVendorStatusBadge(assignment.status)}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground mt-1">No vendor assigned</p>
                  );
                })()}
              </div>

              {/* Tasks for this service */}
              <div>
                <label className="text-sm font-medium">Related Tasks</label>
                {(() => {
                  const itemTasks = tasks.filter(
                    t => t.service_item_id === (selectedServiceItem.service_item_id || selectedServiceItem.item_id)
                  );
                  return itemTasks.length > 0 ? (
                    <div className="space-y-2 mt-2">
                      {itemTasks.map(task => (
                        <div key={task.task_id} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                          <span className="text-sm">{task.title}</span>
                          <Badge variant="outline">{task.status}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground mt-1">No tasks yet</p>
                  );
                })()}
              </div>

              {/* Notes */}
              <div>
                <label className="text-sm font-medium">Notes</label>
                <Textarea
                  value={serviceItemNotes[selectedServiceItem.service_item_id || selectedServiceItem.item_id] || ''}
                  onChange={(e) => setServiceItemNotes({
                    ...serviceItemNotes,
                    [selectedServiceItem.service_item_id || selectedServiceItem.item_id]: e.target.value
                  })}
                  placeholder="Add notes for this service item..."
                  className="mt-1"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowServiceDetailDialog(false)}>Close</Button>
            <Button onClick={() => {
              setEditingTask(null);
              setTaskForm({
                title: '', 
                description: '', 
                status: 'todo', 
                priority: 'medium',
                assignee_id: '', 
                due_date: '', 
                service_item_id: selectedServiceItem?.service_item_id || selectedServiceItem?.item_id
              });
              setShowServiceDetailDialog(false);
              setShowTaskDialog(true);
            }}>
              <Plus className="mr-2 h-4 w-4" /> Add Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purchase Service Dialog */}
      <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              Purchase Service from Vendor
            </DialogTitle>
            <DialogDescription>
              Create a purchase order for {selectedAssignment?.vendor_name} to provide {selectedAssignment?.service_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Vendor & Event Info */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
              <div>
                <label className="text-xs text-muted-foreground">Vendor</label>
                <p className="font-medium">{selectedAssignment?.vendor_name}</p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Service</label>
                <p className="font-medium">{selectedAssignment?.service_name}</p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Event Date</label>
                <p className="font-medium">{formatDate(workspace?.event_date)}</p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Location</label>
                <p className="font-medium">{workspace?.event_location || 'TBD'}</p>
              </div>
            </div>

            <Separator />

            {/* Amount */}
            <div>
              <label className="text-sm font-medium">Amount to Pay Vendor *</label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-lg font-bold">$</span>
                <Input
                  type="number"
                  step="0.01"
                  value={purchaseForm.amount}
                  onChange={(e) => setPurchaseForm({...purchaseForm, amount: e.target.value})}
                  placeholder="0.00"
                  className="max-w-[200px]"
                  data-testid="purchase-amount"
                />
                <span className="text-sm text-muted-foreground">USD</span>
              </div>
              {selectedServiceItem?.total_price && (
                <p className="text-xs text-muted-foreground mt-1">
                  Service value from booking: ${selectedServiceItem.total_price?.toFixed(2)}
                </p>
              )}
            </div>

            {/* Event Time */}
            <div>
              <label className="text-sm font-medium">Event Time / Schedule</label>
              <Input
                value={purchaseForm.event_time}
                onChange={(e) => setPurchaseForm({...purchaseForm, event_time: e.target.value})}
                placeholder="e.g., 5:00 PM - 10:00 PM"
                className="mt-1"
              />
            </div>

            {/* What to Expect */}
            <div>
              <label className="text-sm font-medium">What to Expect (Deliverables from Vendor)</label>
              <Textarea
                value={purchaseForm.what_to_expect}
                onChange={(e) => setPurchaseForm({...purchaseForm, what_to_expect: e.target.value})}
                placeholder="Describe what the vendor will provide: services, equipment, setup details, etc."
                className="mt-1 min-h-[80px]"
              />
            </div>

            {/* What We Provide */}
            <div>
              <label className="text-sm font-medium">What Eventism Will Provide</label>
              <Textarea
                value={purchaseForm.what_we_provide}
                onChange={(e) => setPurchaseForm({...purchaseForm, what_we_provide: e.target.value})}
                placeholder="What will Eventism provide to the vendor? (e.g., equipment, venue access, meals)"
                className="mt-1 min-h-[80px]"
              />
            </div>

            {/* Special Requirements */}
            <div>
              <label className="text-sm font-medium">Special Requirements</label>
              <Textarea
                value={purchaseForm.special_requirements}
                onChange={(e) => setPurchaseForm({...purchaseForm, special_requirements: e.target.value})}
                placeholder="Any special requirements, dress code, certifications needed, etc."
                className="mt-1"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium">Additional Notes</label>
              <Textarea
                value={purchaseForm.notes}
                onChange={(e) => setPurchaseForm({...purchaseForm, notes: e.target.value})}
                placeholder="Any other information for the vendor..."
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowPurchaseDialog(false);
              setSelectedAssignment(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleCreatePurchase} className="bg-green-600 hover:bg-green-700" data-testid="create-purchase-btn">
              <Receipt className="mr-2 h-4 w-4" /> Create Purchase Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
